<template>
  <b-form @submit.prevent="submitForm">
    <b-form-group v-if="importOption === 0">
      <template #label>
        <div class="d-flex justify-content-between">
          <a v-if="sampleFileUrl" :href="sampleFileUrl" target="_blank">
            {{ $t("downloadSampleFile") }}
          </a>
        </div>
      </template>

      <b-form-file
        ref="fileInput"
        accept=".xlsx"
        @change.native="onFileChangeNew"
      />
    </b-form-group>

    <div v-if="formErrorMsg" class="text-danger">
      {{ formErrorMsg }}
    </div>

    <div class="text-right pt-3">
      <b-button
        type="button"
        variant="secondary"
        class="mr-3"
        @click="onClickCancel"
        :disabled="isSubmitting"
      >
        {{ $t("cancel") }}
      </b-button>

      <b-button
        type="submit"
        variant="primary"
        :disabled="!isFileCorrect"
      >
        {{ $t("confirm") }}
      </b-button>
    </div>
  </b-form>
</template>

<script lang="ts">
import { Component, Emit, Prop, Ref } from "vue-property-decorator";
import BaseComponent from "@/components/BaseComponent";
import * as XLSX from "xlsx";
import { VALIDATION_CONFIG } from "./validateConfig";
import { normalizeHeader } from "@/service/util";
@Component
export default class ImportExcelModal extends BaseComponent {
  @Prop({ required: true }) modalId!: string;
  @Prop({ required: true }) activeFID!: any;
  @Prop({ required: true }) typeModal!: "contact" | "user" | "meeting";

  @Ref("fileInput") readonly fileInputPtr!: any;

  excelData: any[] = [];
  formErrorMsg = "";
  isSubmitting = false;
  isFileCorrect = false;
  importOption = 0;

  @Emit("submitted") __submitted() {}

  onClickCancel() {
    this.resetFile();
    this.$bvModal.hide(this.modalId);
  }


  /**
   * Validate cấu trúc file Excel
   */
  validateFileStructure(headers: string[]): boolean {
    
  const config = VALIDATION_CONFIG[this.typeModal];
  if (!config) return false;

  const normalizedHeaders = new Set(
    headers.map(h => normalizeHeader(h))
  );
 
  return config.requiredCols.every((aliases) =>
    aliases.some(alias =>
      normalizedHeaders.has(normalizeHeader(alias))
    )
  );
}


  /**
   * Map row Excel
   */
  mapRowByConfig(row: any): any {
    const config = VALIDATION_CONFIG[this.typeModal];
    if (!config) return row;

    const mapped: any = {};

    const normalizedKeyMap = Object.keys(row).reduce((acc, key) => {
      acc[normalizeHeader(key)] = key;
      return acc;
    }, {} as Record<string, string>);

    config.mapping.forEach(m => {
      const foundAlias = m.fields.find(f =>
        normalizedKeyMap[normalizeHeader(f)]
      );

      const excelKey = foundAlias
        ? normalizedKeyMap[normalizeHeader(foundAlias)]
        : null;

      if (excelKey) {
        // Nếu tìm thấy cột trong Excel -> Gán giá trị
        mapped[m.key] = String(row[excelKey]).trim();
      } else if (m.required) {
        // Nếu KHÔNG thấy cột, nhưng là bắt buộc -> Gán "" để lát nữa validator báo lỗi
        mapped[m.key] = "";
      }
      // Nếu không thấy và không bắt buộc (Optional) -> KHÔNG LÀM GÌ CẢ (không tạo key trong object)
    });

    return mapped;
  }

  /**
   * Đọc file Excel
   */
  onFileChangeNew(e: Event) {
    this.formErrorMsg = "";
    this.excelData = [];
    this.isFileCorrect = false;

    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt: any) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (!rawData.length) {
          this.formErrorMsg = this.$t("notCorrect").toString();
          return;
        }

        const headers = Object.keys(rawData[0]);
        if (!this.validateFileStructure(headers)) {
          this.formErrorMsg = this.$t("notCorrectFormat").toString();
          return;
        }

        this.excelData = rawData.map(row => this.mapRowByConfig(row));
        this.isFileCorrect = true;
      } catch (err) {
        console.error(err);
        this.formErrorMsg = this.$t("errorReadFile").toString();
      }
    };

    reader.readAsArrayBuffer(file);
  }

  async submitForm() {
    if (!this.excelData.length) {
      this.formErrorMsg = this.$t("fileExcelNotData").toString();
      return;
    }

    this.isSubmitting = true;
    try {
      this.$emit("imported", this.excelData);
      this.resetFile();
      this.$bvModal.hide(this.modalId);
    } finally {
      this.isSubmitting = false;
    }
  }

  resetFile() {
    if (this.fileInputPtr?.reset) {
      this.fileInputPtr.reset();
    }
    this.isFileCorrect = false;
  }

  get sampleFileUrl() {
    return VALIDATION_CONFIG[this.typeModal]?.templateFile?.getUrl(
      this.activeFID
    );
  }
}
</script>
